import * as React from 'react';
import { Check } from 'lucide-react';

import { cn } from '../../utils';

type SelectableCardSelectionMode = 'single' | 'multiple';
type SelectableCardOrientation = 'vertical' | 'horizontal';

interface SelectableCardGroupBaseProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'onChange'
> {
  orientation?: SelectableCardOrientation;
}

export interface SelectableCardSingleGroupProps extends SelectableCardGroupBaseProps {
  type?: 'single';
  value?: string;
  onValueChange?: (value: string) => void;
}

export interface SelectableCardMultipleGroupProps extends SelectableCardGroupBaseProps {
  type: 'multiple';
  value: string[];
  onValueChange?: (value: string[]) => void;
}

export type SelectableCardGroupProps =
  SelectableCardSingleGroupProps | SelectableCardMultipleGroupProps;

interface SelectableCardContextValue {
  type: SelectableCardSelectionMode;
  orientation: SelectableCardOrientation;
  isSelected: (value: string) => boolean;
  select: (value: string) => void;
  singleValue?: string;
  firstEnabledValue?: string;
  register: (value: string, disabled: boolean) => () => void;
}

const SelectableCardContext =
  React.createContext<SelectableCardContextValue | null>(null);

const SINGLE_KEYS = [
  'ArrowRight',
  'ArrowLeft',
  'ArrowDown',
  'ArrowUp',
  'Home',
  'End',
] as const;

const SelectableCardGroup = React.forwardRef<
  HTMLDivElement,
  SelectableCardGroupProps
>(
  (
    {
      className,
      type = 'single',
      orientation = 'vertical',
      value,
      onValueChange,
      onKeyDown,
      ...props
    },
    ref,
  ) => {
    const enabledValuesRef = React.useRef<string[]>([]);
    const [firstEnabledValue, setFirstEnabledValue] = React.useState<string>();

    const register = React.useCallback(
      (itemValue: string, disabled: boolean) => {
        if (disabled || enabledValuesRef.current.includes(itemValue))
          return () => undefined;
        enabledValuesRef.current.push(itemValue);
        setFirstEnabledValue(enabledValuesRef.current[0]);
        return () => {
          enabledValuesRef.current = enabledValuesRef.current.filter(
            (candidate) => candidate !== itemValue,
          );
          setFirstEnabledValue(enabledValuesRef.current[0]);
        };
      },
      [],
    );

    const isSelected = React.useCallback(
      (itemValue: string) =>
        type === 'single'
          ? value === itemValue
          : (value as string[]).includes(itemValue),
      [type, value],
    );

    const select = React.useCallback(
      (itemValue: string) => {
        if (type === 'single') {
          (onValueChange as SelectableCardSingleGroupProps['onValueChange'])?.(
            itemValue,
          );
          return;
        }
        const current = value as string[];
        const next = current.includes(itemValue)
          ? current.filter((candidate) => candidate !== itemValue)
          : [...current, itemValue];
        (onValueChange as SelectableCardMultipleGroupProps['onValueChange'])?.(
          next,
        );
      },
      [onValueChange, type, value],
    );

    const context = React.useMemo<SelectableCardContextValue>(
      () => ({
        type,
        orientation,
        isSelected,
        select,
        singleValue:
          type === 'single' ? (value as string | undefined) : undefined,
        firstEnabledValue,
        register,
      }),
      [
        firstEnabledValue,
        isSelected,
        orientation,
        register,
        select,
        type,
        value,
      ],
    );

    return (
      <SelectableCardContext.Provider value={context}>
        <div
          ref={ref}
          role={type === 'single' ? 'radiogroup' : 'group'}
          aria-orientation={orientation}
          className={cn(
            orientation === 'vertical'
              ? 'flex flex-col gap-3'
              : 'grid gap-3 sm:grid-cols-2',
            className,
          )}
          onKeyDown={(event) => {
            onKeyDown?.(event);
            if (
              event.defaultPrevented ||
              type !== 'single' ||
              !SINGLE_KEYS.includes(event.key as (typeof SINGLE_KEYS)[number])
            ) {
              return;
            }

            const cards = Array.from(
              event.currentTarget.querySelectorAll<HTMLButtonElement>(
                '[data-selectable-card]:not(:disabled)',
              ),
            );
            if (cards.length === 0) return;

            event.preventDefault();
            const focusedIndex = cards.indexOf(
              document.activeElement as HTMLButtonElement,
            );
            const selectedIndex = cards.findIndex(
              (card) => card.getAttribute('aria-checked') === 'true',
            );
            const currentIndex =
              focusedIndex >= 0 ? focusedIndex : selectedIndex;
            const lastIndex = cards.length - 1;
            let nextIndex: number;

            if (event.key === 'Home') nextIndex = 0;
            else if (event.key === 'End') nextIndex = lastIndex;
            else if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
              nextIndex =
                currentIndex < 0 ? 0 : (currentIndex + 1) % cards.length;
            } else {
              nextIndex =
                currentIndex < 0
                  ? lastIndex
                  : (currentIndex - 1 + cards.length) % cards.length;
            }

            cards[nextIndex]?.focus();
            cards[nextIndex]?.click();
          }}
          {...props}
        />
      </SelectableCardContext.Provider>
    );
  },
);
SelectableCardGroup.displayName = 'SelectableCardGroup';

export interface SelectableCardProps extends Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  'children' | 'title' | 'value'
> {
  value: string;
  icon?: React.ReactNode;
  iconClassName?: string;
  title: React.ReactNode;
  description?: React.ReactNode;
  selectedDetail?: React.ReactNode;
}

const SelectableCard = React.forwardRef<HTMLButtonElement, SelectableCardProps>(
  (
    {
      className,
      value,
      icon,
      iconClassName,
      title,
      description,
      selectedDetail,
      disabled = false,
      onClick,
      ...props
    },
    forwardedRef,
  ) => {
    const context = React.useContext(SelectableCardContext);
    if (!context) {
      throw new Error('SelectableCard must be used inside SelectableCardGroup');
    }

    const selected = context.isSelected(value);
    const register = context.register;

    React.useEffect(
      () => register(value, disabled),
      [disabled, register, value],
    );

    const tabIndex =
      context.type === 'multiple'
        ? undefined
        : selected ||
            (context.singleValue === undefined &&
              context.firstEnabledValue === value)
          ? 0
          : -1;
    const indicatorPosition =
      context.orientation === 'horizontal'
        ? 'right-4 top-4'
        : 'right-[18px] top-1/2 -translate-y-1/2';

    return (
      <button
        ref={(node) => {
          if (typeof forwardedRef === 'function') forwardedRef(node);
          else if (forwardedRef) forwardedRef.current = node;
        }}
        {...props}
        type="button"
        role={context.type === 'single' ? 'radio' : 'checkbox'}
        aria-checked={selected}
        data-selectable-card=""
        data-state={selected ? 'checked' : 'unchecked'}
        disabled={disabled}
        tabIndex={tabIndex}
        onClick={(event) => {
          onClick?.(event);
          if (!event.defaultPrevented) context.select(value);
        }}
        className={cn(
          'group relative flex w-full cursor-pointer items-start gap-3.5 rounded-2xl border-2 bg-card p-[18px] pr-14 text-left font-body shadow-hard-sm outline-none transition-all duration-200 ease-spring',
          'hover:border-riso-teal/45 hover:shadow-hard focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
          'disabled:pointer-events-none disabled:bg-muted disabled:opacity-60',
          selected && 'border-riso-teal bg-riso-teal-light/50 shadow-hard-teal',
          context.orientation === 'horizontal' && 'flex-col pr-[18px]',
          className,
        )}
      >
        {icon && (
          <span
            aria-hidden="true"
            className={cn(
              'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-riso-teal-light text-riso-teal [&_svg]:h-5 [&_svg]:w-5',
              iconClassName,
            )}
          >
            {icon}
          </span>
        )}
        <span className="flex min-w-0 flex-1 flex-col gap-1.5">
          <span className="font-display text-base font-bold leading-snug text-foreground">
            {title}
          </span>
          {description && (
            <span className="text-sm leading-relaxed text-muted-foreground">
              {description}
            </span>
          )}
          {selected && selectedDetail && (
            <span className="mt-1 flex items-center gap-1.5 text-[13px] font-semibold leading-relaxed text-riso-teal">
              {selectedDetail}
            </span>
          )}
        </span>
        <span
          aria-hidden="true"
          className={cn(
            'absolute flex h-[22px] w-[22px] items-center justify-center rounded-full border-2 transition-all duration-200 ease-spring',
            indicatorPosition,
            selected
              ? 'animate-check-pop border-riso-teal bg-riso-teal text-white shadow-hard-teal'
              : 'border-border bg-card',
          )}
        >
          {selected && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
        </span>
      </button>
    );
  },
);
SelectableCard.displayName = 'SelectableCard';

export { SelectableCardGroup, SelectableCard };
